<script lang="ts">
    import { onMount, tick } from "svelte"
    import { kirbyUrl } from "../../../../api/hooks/config-client"
    import { _fetch } from "../../api"

    let {
        id,
        alt = "",
        class: className = "",
        maxWidth = -1,
        modalable = false,
        disableFocusPosition = false,
    }: {
        id: string | string[]
        maxWidth?: number
        alt?: string
        class?: string
        modalable?: boolean
        disableFocusPosition?: boolean
    } = $props()

    function formatId(id: string | string[]) {
        if (Array.isArray(id)) id = id[0]
        if (!id) return

        const match = id.match(/(['"])file:\/\/([\w-]+)\1/)
        const formattedId = match ? match[2] : id?.replace("file://", "")

        return formattedId
    }
    const normalizedId = $derived.by(() => formatId(id))
    const url = $derived(normalizedId ? `${kirbyUrl}thumbs/${normalizedId}` : "")
    const focusUrl = $derived(normalizedId ? `${kirbyUrl}focus/${normalizedId}` : "")
    function getSrcSet(src: string) {
        if (!src) return
        const imgMaxWidth = 3200 // as per plugin settings
        const divisors = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
        const factors = divisors.map((divisor) => divisor / 10)
        const srcset = factors
            .map((factor) => {
                const w = Math.ceil(imgMaxWidth * factor) // die unterschiedlichen breiten, die je nach dpr nötig sein könnten
                return `${src}?width=${w} ${w}w`
            })
            .join(", ")
        return srcset
    }
    const puffer = 1
    let sizesAttr = $state<string | undefined>(maxWidth > 0 ? `${maxWidth * puffer}px` : undefined)
    let lastMeasuredWidth = $state<number | null>(maxWidth > 0 ? maxWidth * puffer : null)
    let img: HTMLImageElement | null = $state(null)

    let modalImg: HTMLImageElement | null = $state(null)
    let overlayEl: HTMLDivElement | null = $state(null)
    let isOpen = $state(false)

    $effect(() => {
        const currentFocusUrl = focusUrl
        if (!normalizedId || !currentFocusUrl || disableFocusPosition) return

        let cancelled = false

        void _fetch(currentFocusUrl, {
            method: "GET",
        })
            .then((response) => {
                if (response && response.ok) return response.json()
                console.error("Network resp not ok" + JSON.stringify(response))
                return null
            })
            .then((res) => {
                const focus = res?.focus?.focus
                if (!cancelled && focus && img) {
                    img.style.objectPosition = focus
                }
            })
            .catch(() => {})

        return () => {
            cancelled = true
        }
    })

    const updateSizesFromWidth = (width?: number) => {
        const measured = Math.round((width ?? img?.clientWidth ?? 0) * puffer)
        if (measured <= 0) return

        // avoid thrashing when the measured width jitters by sub-pixel values
        if (lastMeasuredWidth !== null && Math.abs(measured - lastMeasuredWidth) < 2) return

        lastMeasuredWidth = measured
        sizesAttr = `${measured}px`
    }

    onMount(() => {
        if (maxWidth > 0) return

        updateSizesFromWidth()
        if (typeof ResizeObserver === "undefined") return

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === img) updateSizesFromWidth(entry.contentRect.width)
            }
        })
        if (img) resizeObserver.observe(img)

        return () => resizeObserver.disconnect()
    })
    // Hilfsfunktion: finale Modalgröße gemäß Formel
    function computeFinalSize(natW: number, natH: number, vw: number, vh: number) {
        const AR = natH / natW
        const Wcap = Math.min(1600, 0.96 * vw)
        const Hcap = 0.92 * vh
        const W_from_H = Hcap / AR
        const Wfinal = Math.min(natW, Wcap, W_from_H)
        const Hfinal = Wfinal * AR
        return { Wfinal, Hfinal }
    }

    async function ensureModalImageReady() {
        if (!modalImg) return
        // Warten bis das Modal-IMG im DOM ist & decodiert wurde
        if (!modalImg.complete) {
            try {
                await modalImg.decode?.()
            } catch {
                /* ignore */
            }
        }
    }

    // Öffnen (nur wenn modalable)
    async function openModal() {
        if (!modalable || !img) return

        const vw = window.innerWidth
        const vh = window.innerHeight
        if (vw < 768) return // kein Modal auf kleinen Screens
        isOpen = true
        await tick()
        if (!img || !modalImg || !overlayEl) return
        await ensureModalImageReady()

        // Ausgang (Thumbnail)
        const r = img.getBoundingClientRect()

        // Natürliche Maße
        const natW = modalImg.naturalWidth || img.naturalWidth || r.width
        const natH = modalImg.naturalHeight || img.naturalHeight || r.height

        // Zielgröße exakt wie im CSS (keine Beschneidung)
        const { Wfinal } = computeFinalSize(natW, natH, vw, vh)

        // FLIP-Parameter
        const scale = r.width / Wfinal
        const tx = r.left + r.width / 2 - vw / 2
        const ty = r.top + r.height / 2 - vh / 2

        // Startzustand

        overlayEl.style.opacity = "0"
        modalImg.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`
        modalImg.style.opacity = "0"
        await tick()
        requestAnimationFrame(() => {
            overlayEl!.style.transition = "opacity 300ms ease"
            modalImg!.style.transition = "transform 300ms cubic-bezier(.2,.8,.2,1), opacity 300ms ease"
            overlayEl!.style.opacity = "1"
            modalImg!.style.opacity = "1"
            modalImg!.style.transform = "translate(0,0) scale(1)"
        })
    }

    function closeModal() {
        if (!img || !modalImg || !overlayEl) {
            isOpen = false
            return
        }

        const r = img.getBoundingClientRect()
        const vw = window.innerWidth
        const vh = window.innerHeight

        const natW = modalImg.naturalWidth || img.naturalWidth || r.width
        const natH = modalImg.naturalHeight || img.naturalHeight || r.height

        const { Wfinal } = computeFinalSize(natW, natH, vw, vh)
        const scale = r.width / Wfinal
        const tx = r.left + r.width / 2 - vw / 2
        const ty = r.top + r.height / 2 - vh / 2

        overlayEl.style.transition = "opacity 300ms ease"
        overlayEl.style.opacity = "0"
        modalImg.style.transition = "transform 300ms cubic-bezier(.2,.8,.2,1), opacity 300ms ease"
        modalImg.style.opacity = "0"
        modalImg.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`
        modalImg.addEventListener(
            "transitionend",
            () => {
                isOpen = false
            },
            { once: true }
        )
    }

    $effect(() => {
        if (!isOpen) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeModal()
        }
        window.addEventListener("keydown", onKey)
        return () => window.removeEventListener("keydown", onKey)
    })
</script>

<picture class="{className}-pic" class:modalable>
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <img
        bind:this={img}
        src={url}
        draggable="false"
        {alt}
        class={className}
        srcset={url ? getSrcSet(url) : undefined}
        data-entry-id={id}
        sizes={sizesAttr}
        onclick={openModal}
    />
</picture>
{#if isOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <div class="modal-overlay" bind:this={overlayEl} role="dialog" aria-modal="true" tabindex="-1" onclick={closeModal}>
        <img
            bind:this={modalImg}
            src={url}
            {alt}
            draggable="false"
            class="modal-image"
            srcset={url ? getSrcSet(url) : undefined}
        />
    </div>
{/if}

<style lang="less" global>
    .no-scroll {
        overflow: hidden;
    }

    /* Thumbnail: nur visuelles Signal, wenn modalable */
    .modalable img {
        @media (min-width: 768px) {
            cursor: zoom-in;
        }
    }

    /* Overlay über allem */
    .modal-overlay {
        position: fixed;
        inset: 0;
        z-index: 2147483647; /* max safe */
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.6);
        /* Fallback falls JS Transition nicht greift */
        transition: opacity 300ms ease;
        img {
            object-fit: contain !important;
            width: auto !important;
        }
    }

    /* Großes Bild im Modal */
    .modal-image {
        max-width: min(90vw, 1600px);
        max-height: 92vh;
        width: auto;
        height: auto;
        object-fit: contain;
        will-change: transform;
        border-radius: 12px;
        /* Fallback-Transition */
        transition:
            transform 300ms cubic-bezier(0.2, 0.8, 0.2, 1),
            opacity 300ms ease;
    }
</style>
